import React, { useState } from 'react';
import { 
  Box, 
  Typography, 
  IconButton, 
  DialogTitle, 
  DialogContent,
  Paper,
  Grid,
  Card,
  CardContent,
  CardActions,
  Button
} from '@mui/material';
import { 
  BrainCircuit, 
  Play, 
  Pause, 
  Stop,
  Settings,
  BarChart3,
  Zap,
  ArrowLeft
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const MLStudio = () => {
  const navigate = useNavigate();

  const handleBackToPortal = () => {
    navigate('/portal');
  };

  return (
    <Box sx={{ height: '100vh', display: 'flex', flexDirection: 'column' }}>
      {/* Header */}
      <DialogTitle sx={{ 
        display: 'flex', 
        alignItems: 'center', 
        p: 2, 
        borderBottom: '1px solid #eee', 
        background: '#f7faff',
        minHeight: '64px'
      }}>
        <IconButton 
          edge="start" 
          color="inherit" 
          onClick={handleBackToPortal} 
          aria-label="back to portal" 
          sx={{ mr: 2 }}
        >
          <ArrowLeft />
        </IconButton>
        <Typography variant="h6" sx={{ fontWeight: 600 }}>
          ML Studio
        </Typography>
      </DialogTitle>

      {/* Content */}
      <DialogContent sx={{ p: 3, height: '100%', overflowY: 'auto' }}>
        <Grid container spacing={3}>
          {/* Welcome Section */}
          <Grid item xs={12}>
            <Paper sx={{ p: 3, background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', color: 'white' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <BrainCircuit size={32} style={{ marginRight: '12px' }} />
                <Typography variant="h5" sx={{ fontWeight: 600 }}>
                  Welcome to ML Studio
                </Typography>
              </Box>
              <Typography variant="body1">
                Build, train, and deploy machine learning models for 3D computer vision. 
                Start with a template or create your own custom pipeline.
              </Typography>
            </Paper>
          </Grid>

          {/* Quick Start Templates */}
          <Grid item xs={12}>
            <Typography variant="h6" sx={{ mb: 2, fontWeight: 600, color: '#2D5EFF' }}>
              Quick Start Templates
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} md={4}>
                <Card sx={{ height: '100%', cursor: 'pointer', '&:hover': { boxShadow: 4 } }}>
                  <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                      <BrainCircuit size={24} color="#2D5EFF" style={{ marginRight: '8px' }} />
                      <Typography variant="h6" sx={{ fontWeight: 600 }}>
                        Point Cloud Classification
                      </Typography>
                    </Box>
                    <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
                      Classify objects in 3D point clouds using deep learning. Perfect for autonomous vehicles and robotics.
                    </Typography>
                    <Button 
                      variant="contained" 
                      startIcon={<Play size={16} />}
                      sx={{ backgroundColor: '#2D5EFF' }}
                    >
                      Start Project
                    </Button>
                  </CardContent>
                </Card>
              </Grid>

              <Grid item xs={12} md={4}>
                <Card sx={{ height: '100%', cursor: 'pointer', '&:hover': { boxShadow: 4 } }}>
                  <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                      <BarChart3 size={24} color="#2D5EFF" style={{ marginRight: '8px' }} />
                      <Typography variant="h6" sx={{ fontWeight: 600 }}>
                        3D Object Detection
                      </Typography>
                    </Box>
                    <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
                      Detect and localize objects in 3D space. Ideal for scene understanding and spatial analysis.
                    </Typography>
                    <Button 
                      variant="contained" 
                      startIcon={<Play size={16} />}
                      sx={{ backgroundColor: '#2D5EFF' }}
                    >
                      Start Project
                    </Button>
                  </CardContent>
                </Card>
              </Grid>

              <Grid item xs={12} md={4}>
                <Card sx={{ height: '100%', cursor: 'pointer', '&:hover': { boxShadow: 4 } }}>
                  <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                      <Zap size={24} color="#2D5EFF" style={{ marginRight: '8px' }} />
                      <Typography variant="h6" sx={{ fontWeight: 600 }}>
                        Point Cloud Segmentation
                      </Typography>
                    </Box>
                    <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
                      Segment point clouds into meaningful parts. Great for medical imaging and industrial inspection.
                    </Typography>
                    <Button 
                      variant="contained" 
                      startIcon={<Play size={16} />}
                      sx={{ backgroundColor: '#2D5EFF' }}
                    >
                      Start Project
                    </Button>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          </Grid>

          {/* Recent Projects */}
          <Grid item xs={12}>
            <Typography variant="h6" sx={{ mb: 2, fontWeight: 600, color: '#2D5EFF' }}>
              Recent Projects
            </Typography>
            <Paper sx={{ p: 2, background: '#f8f9fa' }}>
              <Typography variant="body2" color="textSecondary" sx={{ textAlign: 'center', py: 4 }}>
                No recent projects found. Start your first ML project using one of the templates above.
              </Typography>
            </Paper>
          </Grid>

          {/* Quick Actions */}
          <Grid item xs={12}>
            <Typography variant="h6" sx={{ mb: 2, fontWeight: 600, color: '#2D5EFF' }}>
              Quick Actions
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6} md={3}>
                <Card sx={{ textAlign: 'center', cursor: 'pointer', '&:hover': { boxShadow: 4 } }}>
                  <CardContent>
                    <Play size={32} color="#2D5EFF" style={{ marginBottom: '8px' }} />
                    <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                      New Project
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                      Create from scratch
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>

              <Grid item xs={12} sm={6} md={3}>
                <Card sx={{ textAlign: 'center', cursor: 'pointer', '&:hover': { boxShadow: 4 } }}>
                  <CardContent>
                    <Settings size={32} color="#2D5EFF" style={{ marginBottom: '8px' }} />
                    <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                      Import Model
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                      Load existing model
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>

              <Grid item xs={12} sm={6} md={3}>
                <Card sx={{ textAlign: 'center', cursor: 'pointer', '&:hover': { boxShadow: 4 } }}>
                  <CardContent>
                    <BarChart3 size={32} color="#2D5EFF" style={{ marginBottom: '8px' }} />
                    <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                      View Analytics
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                      Model performance
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>

              <Grid item xs={12} sm={6} md={3}>
                <Card sx={{ textAlign: 'center', cursor: 'pointer', '&:hover': { boxShadow: 4 } }}>
                  <CardContent>
                    <BrainCircuit size={32} color="#2D5EFF" style={{ marginBottom: '8px' }} />
                    <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                      AutoML
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                      Automated training
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </DialogContent>
    </Box>
  );
};

export default MLStudio; 